import PackTier from './-PackTier';
import { getRouteApi, useParams } from '@tanstack/react-router';
import { useElementMap } from '~/util/hooks/useElementMap';
import { useAutoScrollIntoView } from '~/util/hooks/useAutoScrollIntoView';
import { NotFoundComponent } from '~/components/DefaultNotFound';

const Route = getRouteApi('/packs');

export default function PackTiersList({
    viewportRef,
    onPackSelect,
}: {
    viewportRef: React.RefObject<HTMLDivElement | null>;
    onPackSelect?: () => void;
}) {
    const { search, sort, reverse, filters } = Route.useSearch({
        select: ({ search, sort, reverse, filters }) => ({
            search,
            sort,
            reverse,
            filters,
        }),
    });
    const { packid } = useParams({ strict: false });

    const packtiers = Route.useLoaderData();

    const searchLower = search.toLowerCase();
    const filteredTiers = packtiers.reduce<typeof packtiers>((tiers, tier) => {
        const packs = tier.packs
            .filter(
                (pack) =>
                    pack.name.toLowerCase().includes(searchLower) &&
                    (!filters.progress_only ||
                        pack.levels.some((level) => level.completed_by_user)) &&
                    Object.keys(filters.levels).every(
                        (levelid) =>
                            pack.levels.some(
                                (level) => level.id === levelid,
                            ) === filters.levels[levelid],
                    ),
            )
            .sort((packA, packB) => {
                if (sort === 'name') {
                    return !reverse
                        ? packA.name.localeCompare(packB.name)
                        : packB.name.localeCompare(packA.name);
                }
                if (sort === 'points') {
                    return !reverse
                        ? packB.points - packA.points
                        : packA.points - packB.points;
                }
                return 0;
            });

        if (packs.length > 0) {
            tiers.push({ ...tier, packs });
        }

        return tiers;
    }, []);

    const { getElement: getTierElement, registerElement: registerTierElement } =
        useElementMap<string, HTMLDivElement>();

    const selectedTierId = packid
        ? (filteredTiers.find((tier) =>
              tier.packs.some((pack) => pack.id === packid),
          )?.id ?? null)
        : null;

    useAutoScrollIntoView({
        viewportRef,
        enabled: selectedTierId != null,
        scrollKey: `${selectedTierId}|${search}`,
        getTarget: () =>
            selectedTierId ? getTierElement(selectedTierId) : null,
    });

    if (filteredTiers.length === 0) {
        return (
            <div className="px-4 lg:px-8 py-8">
                <NotFoundComponent customNotFound="No packs found." />
            </div>
        );
    }

    return (
        <div className="flex flex-col md:grid md:grid-cols-2 gap-3 px-4 lg:px-8">
            {filteredTiers.map((tier) => (
                <PackTier
                    key={tier.id}
                    tier={tier}
                    ref={registerTierElement(tier.id)}
                    onPackSelect={onPackSelect}
                />
            ))}
        </div>
    );
}
